<template>
  <div class="vz-confirm-code">
    <vz-loader :status-loader="loader"/>

    <div
      v-if="message && !error"
      class="vz-confirm-code-message"
      style="word-break: break-word"
      v-html="message"
    />

    <div class="vz-confirm-code-input mt-20">
      <input
        v-for="i in length"
        ref="input"
        inputmode="numeric"
        name="sms"
        :autocomplete="i === 1 ? 'one-time-code' : undefined"
        :class="{'has-error': error || isShowError}"
        :value="arrayValue[i - 1]"
        @focus="onFocus($event, i - 1)"
        @input="onInput($event, i - 1)"
        @paste="onPaste($event, i - 1)"
        @keydown.delete="onDelete($event, i - 1)"
      />
    </div>

    <vz-invalid-message
      v-if="error || isShowError"
      class="vz-position-static vz-align-center flex-column mt-10"
    >
      <div v-if="error">{{ error }}</div>
      <div v-if="errorText">{{ errorText }}</div>
    </vz-invalid-message>

    <div
      class="fs-12 mt-20 flex flex-align-items-center"
      :class="{
        'vz-cursor-pointer hovered-span': localTimeout <= 0,
        'color-lower': localTimeout > 0
      }"
      @click="sendConfirmCode"
    >
      <span>
        {{ repeatConfirmCodeText }}
      </span>
      <vz-icon
        v-if="localTimeout <= 0"
        name="refresh"
        class="ml-5"
        style="text-decoration: unset;"
      />
    </div>

    <public-contacts
      v-if="!isHideContacts"
      class="mt-15"
    />
  </div>
</template>

<script setup lang="ts">
import {Timeout, ValidationRule} from '~/types/types';
import {PropType, onMounted, nextTick} from 'vue';
import VzIcon from '~/components/ui-kit/vz-icon.vue';
import PublicContacts from '~/components/public/public-contacts.vue';
import VzInvalidMessage from "~/components/ui-kit/vz-invalid-message.vue";

const emit = defineEmits(['update:modelValue', 'submit', 'send'])
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  },
  length: {
    type: Number,
    default: 4
  },
  timeout: {
    type: Number,
    default: null
  },
  rules: {
    type: Array as PropType<ValidationRule[]>,
    default: () => ([])
  },
  loader: {
    type: Boolean,
    default: false
  },
  isHideContacts: {
    type: Boolean,
    default: false
  },
  isOtp: {
    type: Boolean,
    default: true
  }
})

let timer = ref<Timeout>()

const input = ref<HTMLInputElement[]>([])
const localTimeout = ref(0)

const {isShowError, errorText, doValidate} = useValidateble()

async function checkError(silent: boolean = false): Promise<boolean> {
  return await doValidate(props.modelValue, props.rules, silent)
}

const arrayValue = computed(() => {
  return props.modelValue?.split('')
})

const repeatConfirmCodeText = computed(() => {
  if (localTimeout.value > 0) {
    return `Отправить код повторно через ${localTimeout.value} секунд`
  }

  return 'Отправить код повторно'
})

function onInput(e: any, i: number) {
  const value = (e.target.value?.length === 1 ? e.target.value : e.data)
    ?.replace(/\D/g, '')

  e.target.value = value
  changeChar(value, i)
  nextTick(() => {
    if (arrayValue.value.length < props.length) {
      onFocus(e, i + 1)
    } else {
      emit('submit')
      e?.target?.blur?.()
    }
  })
}

function onFocus(e: any, i: number): void {
  if (input.value[i]?.value) {
    input.value[i].select()
    return
  }
  if (i > 0 && !arrayValue.value[i - 1]) {
    return onFocus(e, i - 1)
  }
  if (i > arrayValue.value.length) {
    e.target.select()
  } else if (arrayValue.value.length < props.length) {
    input.value[arrayValue.value.length].focus()
  }
}

async function onPaste(e: any, i: number) {
  // Stop data actually being pasted into div
  e.stopPropagation()
  e.preventDefault()
  // Get pasted data via clipboard API
  const clipboardData = e.clipboardData || (window as any).clipboardData
  const pastedData = clipboardData.getData('Text')?.replace(/\D/g, '')

  if (pastedData.length == props.length) {
    emit('update:modelValue', pastedData)
    await nextTick()
    e?.target?.blur?.()
    emit('submit')
  } else if (pastedData.length === 1) {
    changeChar(pastedData, i)
    await nextTick()
    if (i < props.length) {
      onFocus(e, i + 1)
    } else {
      emit('submit')
      e?.target?.blur?.()
    }
  }
}

function onDelete(e: any, i: number) {
  if (arrayValue.value[i]?.toString()) {
    e.preventDefault()
    changeChar(undefined, i)
  } else {
    onFocus(e, i - 1)
  }
}

function changeChar(value: string | undefined, i: number) {
  const newArrayValue = {...arrayValue.value, [i]: value}
  emit('update:modelValue', Object.values(newArrayValue).join(''))
  nextTick(() => {
    checkError()
  })
}

function sendConfirmCode(): void {
  if (localTimeout.value > 0) {
    return
  }
  emit('send')
  nextTick(() => {
    input.value[0].focus()
  })
}

watch(() => props.timeout,
  () => {
    localTimeout.value = props.timeout
    if (localTimeout.value <= 0) {
      return
    }

    if (timer.value) {
      clearInterval(timer.value)
    }

    timer.value = setInterval(() => {
      localTimeout.value = (localTimeout.value || 0) - 1
      if (localTimeout.value <= 0 && timer.value) {
        clearTimeout(timer.value)
      }
    }, 1000)
  },
  {immediate: true}
)

onMounted(() => {
  if(props.length <= arrayValue.value.length) {
    return
  }

  input.value[arrayValue.value.length].focus()

  if(!props.isOtp) {
    return
  }

  if ("credentials" in navigator) {
    navigator.credentials?.get({
      otp: {transport: ['sms']}
    })
      .then(async (otp) => {
        console.log('otp then', otp)
        emit('update:modelValue', otp?.code)
        await nextTick()
        emit('submit')
      }).catch((e) => {
      console.log('otp catch', e)
    })
  } else {
    console.log('otp credentials не разрешен')
  }
})

defineExpose({
  localTimeout,
  isShowError,
  checkError
})
</script>

<style lang="scss" scoped>
.hovered-span {
  &:hover {
    span {
      text-decoration: underline;
    }
  }
}

.vz-confirm-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  &-message {
    font-size: 16px;
    color: $font-lower;
    text-align: center;
    margin-top: 20px;
  }

  &-input {
    display: flex;
    justify-content: center;
    font-size: 16px;

    input {
      background-color: $component-background;
      border: 1px solid $border-gray;
      border-radius: 4px;
      width: 40px;
      height: 40px;
      padding: 14px;
      line-height: 19px;
      color: $font-dark;

      &.has-error {
        border: 1px solid $error;
      }

      &:not(:first-child) {
        margin-left: 15px;
      }
    }
  }
}
</style>
